import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles } from "lucide-react";

const STORAGE_KEY = "ra_boatclub_banner_dismissed_v1";

/**
 * Site-wide marketing announcement banner — mounted directly BELOW <Nav/>
 * inside the public Layout.
 *
 * - Hidden on /boat-club (don't nag someone who's already there) and on
 *   /admin/* (internal surfaces).
 * - Dismissal persists to localStorage under a versioned key so a future
 *   announcement can re-enable itself by bumping the version suffix.
 * - Non-sticky: scrolls out of view with the page; only <Nav/> stays sticky.
 * - Entire banner body is clickable (navigates to /boat-club); the inline
 *   coral CTA button is an additive, more obvious target. Dismiss "×" is a
 *   true sibling so it doesn't nest inside the <a>.
 */
export default function AnnouncementBanner() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (pathname === "/boat-club" || pathname.startsWith("/admin")) {
      setVisible(false);
      return;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) === "true") {
        setVisible(false);
        return;
      }
    } catch {
      /* storage disabled — show anyway */
    }
    setLeaving(false);
    setVisible(true);
  }, [pathname]);

  if (!visible) return null;

  const onDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      /* ignore */
    }
    setLeaving(true);
    window.setTimeout(() => setVisible(false), 160);
  };

  return (
    <div
      role="region"
      aria-label="New feature announcement"
      data-testid="announcement-banner"
      className={`relative border-b transition-opacity duration-150 ${
        leaving ? "opacity-0" : "opacity-100"
      }`}
      style={{ backgroundColor: "#E8F1F4", borderBottomColor: "#CBD5DC" }}
    >
      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6 py-4 sm:py-5">
        {/* Row 1 (mobile) / full row (desktop) — flex of Link + button + dismiss */}
        <div className="flex items-center gap-3 sm:gap-5 pr-10 sm:pr-12">
          <Link
            to="/boat-club"
            data-testid="announcement-banner-link"
            className="group flex min-w-0 flex-1 items-center gap-3 sm:gap-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ra-ocean focus-visible:ring-offset-2 focus-visible:ring-offset-[#E8F1F4] rounded-md"
          >
            <span
              className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.14em] text-white"
              style={{ backgroundColor: "#B83B2E" }}
              data-testid="announcement-banner-pill"
            >
              <Sparkles size={11} strokeWidth={2.5} />
              New
            </span>
            <span
              className="min-w-0 flex-1 truncate font-display text-[15px] sm:text-[17px] font-semibold leading-snug text-[#0F1F24]"
              data-testid="announcement-banner-copy"
            >
              Run your boat club on the same platform as your rentals
            </span>
            {/* CTA button — desktop only; mobile shows it on row 2 below */}
            <span
              className="hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-transform group-hover:translate-x-0.5"
              style={{ backgroundColor: "#CB5248" }}
              data-testid="announcement-banner-cta"
            >
              Explore Boat Clubs
              <span aria-hidden="true">→</span>
            </span>
          </Link>
        </div>

        {/* Row 2 — mobile-only full-width CTA (keeps touch target real) */}
        <div className="mt-3 sm:hidden">
          <Link
            to="/boat-club"
            data-testid="announcement-banner-cta-mobile"
            className="flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ra-ocean focus-visible:ring-offset-2"
            style={{ backgroundColor: "#CB5248" }}
          >
            Explore Boat Clubs
            <span aria-hidden="true">→</span>
          </Link>
        </div>

        {/* Dismiss "×" — absolute sibling so it doesn't nest inside <a> */}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          data-testid="announcement-banner-dismiss"
          className="absolute right-3 sm:right-5 top-3 sm:top-1/2 sm:-translate-y-1/2 rounded-full p-1.5 text-[#0F1F24]/55 hover:bg-ra-ocean/15 hover:text-[#0F1F24] focus:outline-none focus-visible:ring-2 focus-visible:ring-ra-ocean transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
